import { Space, Tag, Tooltip } from "antd";
import { TimestampCell } from "../../common/TimestampCell";
import { CopyableText } from "../../common/CopyableText";
import { WaterTypeBadge } from "../../common/WaterTypeBadge";
import { ChartIconButton } from "../ChartIconButton";
import {
  formatBatteryValue,
  getReadoutIsLate,
  getReadoutStatus,
  getReadoutTimestamp,
  getSnapshotValue,
  splitMeterStatus,
  statusSortValue,
} from "../readoutFormat";

export const waterColumns = (concentratorID: string) => [
  {
    title: "Meter Name",
    dataIndex: "name",
    key: "name",
    width: 200,
    sorter: true,
    render: (name: string, record: any) => (
      <Space>
        <CopyableText text={name} />
        <ChartIconButton
          concentratorID={concentratorID}
          meterID={record.id}
        />
      </Space>
    ),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 88,
    sorter: (a: any, b: any) =>
      statusSortValue(getReadoutStatus(a), getReadoutIsLate(a)) -
      statusSortValue(getReadoutStatus(b), getReadoutIsLate(b)),
    render: (_: unknown, record: any) => {
      const parsed = splitMeterStatus(getReadoutStatus(record), getReadoutIsLate(record));
      const freshnessTag = (
        <Tag color={parsed.isLate ? "red" : "green"}>
          {parsed.isLate ? "LATE" : "OK"}
        </Tag>
      );
      if (parsed.vendor.length === 0) {
        return freshnessTag;
      }

      const vendorTag = (
        <Tooltip title={parsed.vendor.join(", ")}>
          <Tag color="gold">VENDOR</Tag>
        </Tooltip>
      );

      if (parsed.isLate) {
        return (
          <Space direction="vertical" size={2}>
            {freshnessTag}
            {vendorTag}
          </Space>
        );
      }
      return vendorTag;
    },
  },
  {
    title: "Water [m³]",
    dataIndex: "latest_readout_value",
    key: "latest_readout_value",
    width: 120,
    sorter: true,
    render: (_value: number | null, record: any) => {
      const waterValue = getSnapshotValue(record, "water");
      return waterValue == null ? "N/A" : waterValue.toFixed(2);
    },
  },
  {
    title: "Timestamp",
    dataIndex: "latest_readout_timestamp",
    key: "latest_readout_timestamp",
    width: 160,
    sorter: true,
    render: (_value: string, record: any) => (
      <TimestampCell value={getReadoutTimestamp(record) ?? ""} />
    ),
  },
  {
    title: "UUID",
    dataIndex: "uuid",
    key: "uuid",
    width: 150,
    sorter: true,
    render: (value: string | null) => (
      <CopyableText text={value} display={value ? `${value.slice(0, 8)}...` : undefined} />
    ),
  },
  {
    title: "Address",
    dataIndex: "address",
    key: "address",
    width: 150,
    sorter: true,
    render: (value: string | null) => value ? value : "N/A",
  },
  {
    title: "Serial",
    dataIndex: "serial",
    key: "serial",
    width: 160,
    sorter: true,
    render: (value: string | null) => value ? value : "N/A",
  },
  {
    title: "Water Type",
    dataIndex: "hot",
    key: "hot",
    width: 110,
    sorter: true,
    render: (value: boolean | null) => <WaterTypeBadge isHot={value} />,
  },
  {
    title: "Battery",
    dataIndex: "battery_value",
    key: "battery_value",
    width: 120,
    sorter: true,
    render: (_value: number | null, record: any) =>
      formatBatteryValue(getSnapshotValue(record, "battery_voltage")),
  },
];
